<script setup lang="ts">
/**
 * PAGE CAGNOTTE — corps de la future page cagnotte, en sections titrées avec un
 * sommaire collant (façon Dift) : le projet, actualités (cartes avec image),
 * à quoi serviront les dons (texte facultatif de l'asso), donateurs (la VRAIE
 * liste à chargement infini, slot `donateurs`), puis coup de cœur Donoor et
 * mot de l'asso (slot `apres-donateurs`). L'association est en bas de page.
 */
import { stripHtml } from "@donoor/utils/string";
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import { useSanitizeHtml } from "~/composables/useSanitizeHtml";
import PageCagnotteTexteRepliable from "./PageCagnotteTexteRepliable.vue";
import PageCagnottePhotos from "./PageCagnottePhotos.vue";
import type { ActualiteCagnotte, CagnotteAffichee } from "./types";
import { defilerDansDemoAccueil, zoneDemoAccueil } from "~/utils/demoAccueil";

const props = defineProps<{
  cagnotte: CagnotteAffichee;
  actualites: ActualiteCagnotte[];
  /** « À quoi serviront les dons » : HTML mis en forme comme la description, facultatif. */
  utilisationDons: string;
  totalDonateurs: number;
  /** Toutes les photos (bannière + galerie) ; vide = pas de rangée « Photos ». */
  photos: string[];
  /** Le bloc #association existe plus bas : entrée dans le sommaire. */
  avecAssociation: boolean;
  /** Vidéo YouTube de la cagnotte (adresse d'intégration), en tête du projet. */
  videoUrl?: string | null;
}>();

const { sanitize } = useSanitizeHtml();
const { t } = useI18n();
const { getImageUrl } = useRechercheHelpers();
const { formatDate } = useFormatLangue();

const avecUtilisationDons = computed(() => stripHtml(props.utilisationDons).trim().length > 0);
const actualitesTriees = computed(() =>
  [...props.actualites].sort((a, b) => String(b.date_posted).localeCompare(String(a.date_posted))),
);

/** Image d'une actualité : la 1re image de son texte, sinon la photo de la cagnotte. */
function imageActualite(a: ActualiteCagnotte): string | null {
  const src = /<img[^>]+src="([^"]+)"/i.exec(a.content)?.[1];
  if (src) return src;
  return props.cagnotte.image ? getImageUrl(props.cagnotte.image, undefined, 800) : null;
}
const extrait = (a: ActualiteCagnotte) => stripHtml(a.content);

// Actualité ouverte en grand (ou toutes, avec « Voir tout »).
const ouverte = ref<ActualiteCagnotte | "toutes" | null>(null);
watch(ouverte, (v) => {
  if (import.meta.client) document.body.style.overflow = v ? "hidden" : "";
});

// ─── Sommaire collant : défilement doux + section active ────────────────
const sections = computed(() =>
  [
    { id: "projet", label: t("campagne.pageCagnotte.sectionProjet"), visible: true },
    { id: "dons", label: t("campagne.pageCagnotte.sectionDons"), visible: avecUtilisationDons.value },
    { id: "donateurs", label: t("campagne.pageCagnotte.sectionDonateurs"), visible: props.totalDonateurs > 0 },
    { id: "photos", label: t("campagne.pageCagnotte.sectionPhotos"), visible: props.photos.length > 0 },
    { id: "actualites", label: t("campagne.pageCagnotte.sectionActualites"), visible: props.actualites.length > 0 },
    { id: "association", label: t("campagne.pageCagnotte.sectionAssociation"), visible: props.avecAssociation },
  ].filter((s) => s.visible),
);
const active = ref("projet");
function aller(id: string) {
  active.value = id;
  const section = document.getElementById(id);
  // Démo de la home : on défile DANS la démo, et l'adresse (celle de la home)
  // ne prend pas d'ancre.
  if (section && defilerDansDemoAccueil(section)) return;
  section?.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", `#${id}`);
}
let observer: IntersectionObserver | null = null;
function observerSections() {
  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries) => {
      const visibles = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visibles[0]) active.value = visibles[0].target.id;
    },
    // Démo de la home : sections suivies dans la zone de la démo, pas la fenêtre.
    { root: zoneDemoAccueil(document.getElementById(sections.value[0]?.id ?? "")), rootMargin: "-120px 0px -55% 0px" },
  );
  for (const s of sections.value) {
    const el = document.getElementById(s.id);
    if (el) observer.observe(el);
  }
}
onMounted(observerSections);
watch(sections, () => nextTick(observerSections));
onBeforeUnmount(() => observer?.disconnect());

const titreStyle =
  "font-family: var(--font-body) !important; font-weight: 700 !important; line-height: 1.2 !important; letter-spacing: -0.015em !important";
</script>

<template>
  <div>
    <!-- Sommaire collant -->
    <nav class="sticky top-0 z-20 -mx-5 px-5 sm:mx-0 sm:px-0 bg-white/85 backdrop-blur-md border-b border-gray-100">
      <ul class="flex gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <li v-for="s in sections" :key="s.id" class="shrink-0">
          <a
            :href="`#${s.id}`"
            class="block py-4 text-sm no-underline border-b-2 transition-colors"
            :class="active === s.id ? 'border-(--color-main2) text-(--color-ink)' : 'border-transparent text-gray-500 hover:text-(--color-ink)'"
            style="font-weight: 600 !important"
            @click.prevent="aller(s.id)"
          >{{ s.label }}</a>
        </li>
      </ul>
    </nav>

    <!-- Le projet -->
    <section id="projet" class="scroll-mt-20 pt-10">
      <h2 class="text-2xl text-(--color-ink)" :style="titreStyle">{{ $t("campagne.pageCagnotte.sectionProjet") }}</h2>
      <!-- Vidéo de présentation (comme sur l'ancienne page), avant le texte -->
      <div v-if="videoUrl" class="mt-5 aspect-video overflow-hidden rounded-3xl bg-black">
        <iframe
          :src="videoUrl"
          class="h-full w-full"
          title="YouTube"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </div>
      <!-- Repliée au-delà de ~420 px, comme un article. -->
      <PageCagnotteTexteRepliable v-if="cagnotte.description" class="mt-5" :html="cagnotte.description" />

    </section>

    <!-- À quoi serviront les dons : texte facultatif de l'asso (façon Dift) -->
    <section v-if="avecUtilisationDons" id="dons" class="scroll-mt-20 pt-14">
      <h2 class="text-2xl text-(--color-ink)" :style="titreStyle">{{ $t("campagne.pageCagnotte.sectionDons") }}</h2>
      <!-- Mis en forme comme la description, même repli « Lire la suite ». -->
      <PageCagnotteTexteRepliable class="mt-6" :html="utilisationDons" :hauteur-max="320" />
    </section>

    <!-- Donateurs : la vraie liste (chargement infini, tri, messages) -->
    <section v-if="totalDonateurs > 0" id="donateurs" class="scroll-mt-20 pt-14">
      <slot name="donateurs" />
    </section>

    <!-- Photos : toutes les images sur une ligne qui défile -->
    <section v-if="photos.length" id="photos" class="scroll-mt-20 pt-14">
      <PageCagnottePhotos :images="photos" :titre-style="titreStyle" />
    </section>

    <!-- Actualités : cartes image + date + titre + extrait (façon Dift) -->
    <section v-if="actualitesTriees.length" id="actualites" class="scroll-mt-20 pt-14">
      <div class="flex items-baseline justify-between gap-4">
        <h2 class="text-2xl text-(--color-ink)" :style="titreStyle">{{ $t("campagne.pageCagnotte.sectionActualites") }}</h2>
        <button
          v-if="actualitesTriees.length > 2"
          type="button"
          class="text-[15px] text-(--color-ink) underline underline-offset-4 decoration-gray-300 hover:decoration-(--color-ink)"
          style="font-weight: 500 !important"
          @click="ouverte = 'toutes'"
        >{{ $t("campagne.pageCagnotte.voirTout") }}</button>
      </div>
      <div class="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-10">
        <button
          v-for="(a, i) in actualitesTriees.slice(0, 2)"
          :key="i"
          type="button"
          class="group text-left"
          @click="ouverte = a"
        >
          <div class="aspect-[16/6] rounded-xl overflow-hidden bg-[#eef2ff]">
            <img
              v-if="imageActualite(a)"
              :src="imageActualite(a) ?? undefined"
              alt=""
              class="w-full h-full object-cover transition-[scale] duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
          </div>
          <p class="mt-4 text-[13px] text-gray-400">{{ formatDate(a.date_posted, { format: "short" }) }}</p>
          <p class="mt-1.5 text-base leading-snug text-(--color-ink) group-hover:text-(--color-main2) transition-colors" style="font-weight: 600 !important">
            {{ a.title || extrait(a).slice(0, 80) }}
          </p>
          <p class="mt-1.5 text-sm leading-relaxed text-gray-500 line-clamp-3">{{ extrait(a) }}</p>
        </button>
      </div>
    </section>

    <!-- Coup de cœur Donoor + mot d'une personne de l'asso -->
    <slot name="apres-donateurs" />

    <!-- Actualité(s) en grand -->
    <Teleport to="body">
      <Transition enter-active-class="transition duration-200" enter-from-class="opacity-0" leave-active-class="transition duration-150" leave-to-class="opacity-0">
        <div v-if="ouverte" class="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6" @click.self="ouverte = null">
          <div class="w-full sm:max-w-2xl max-h-[88vh] overflow-y-auto bg-white rounded-t-[28px] sm:rounded-[28px] p-6 sm:p-8">
            <div class="flex items-center justify-between gap-4">
              <p class="text-xl text-(--color-ink)" :style="titreStyle">{{ $t("campagne.pageCagnotte.sectionActualites") }}</p>
              <button type="button" class="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" :aria-label="$t('commun.actions.fermer')" @click="ouverte = null">
                <UIcon name="i-lucide-x" class="size-5" />
              </button>
            </div>
            <article
              v-for="(a, i) in ouverte === 'toutes' ? actualitesTriees : [ouverte]"
              :key="i"
              class="pt-6 mt-6 border-t border-gray-100 first-of-type:border-0 first-of-type:mt-2"
            >
              <p class="text-sm text-gray-400">{{ formatDate(a.date_posted, { format: "medium" }) }}</p>
              <p v-if="a.title" class="mt-1 text-lg text-(--color-ink)" style="font-weight: 700 !important">{{ a.title }}</p>
              <!-- eslint-disable vue/no-v-html -->
              <!-- Reason: contenu sanitizé via sanitize() (DOMPurify) -->
              <div class="prose prose-gray max-w-none mt-3" v-html="sanitize(a.content)" />
              <!-- eslint-enable vue/no-v-html -->
            </article>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
